<template>
  <div :class="fieldClass">
    <el-radio-group
      :value="value"
      @input="handleInput"
      @change="handleChange"
      :disabled="field.isDisabled"
      :size="getProperty('size', 'medium')"
    >
      <template v-if="getProperty('buttonStyle', false)">
        <el-radio-button
          v-for="option in availableOptions"
          :key="option.value || option.code"
          :label="option.value || option.code"
          :disabled="option.disabled"
        >
          {{ translate(option.label || option.libelle || option.name) }}
        </el-radio-button>
      </template>
      <template v-else>
        <el-radio
          v-for="option in availableOptions"
          :key="option.value || option.code"
          :label="option.value || option.code"
          :disabled="option.disabled"
          :class="radioClass"
        >
          {{ translate(option.label || option.libelle || option.name) }}
        </el-radio>
      </template>
    </el-radio-group>
    
    <!-- Aide contextuelle -->
    <div v-if="field.helpText" class="field-help-text">
      <small class="text-muted">{{ $t(field.helpText) }}</small>
    </div>
  </div>
</template>

<script>
export default {
  name: 'GenericFieldRadioAdvanced',
  props: {
    field: {
      type: Object,
      required: true
    },
    value: {
      type: [String, Number, Boolean, Array],
      default: null,
      validator(value) {
        // Protection contre les erreurs de type
        if (Array.isArray(value)) {
          console.warn(`🚨 SearchFieldRadio reçoit un Array au lieu d'une valeur simple:`, value)
          return false
        }
        return true
      }
    }
  },
  computed: {
    fieldClass() {
      return {
        'search-field': true,
        'search-field-radio': true,
        'search-field-required': this.field.isRequired,
        'search-field-disabled': this.field.isDisabled
      }
    },
    radioClass() {
      return {
        'radio-compact': this.getProperty('compact', false),
        'radio-inline': this.getProperty('inline', true)
      }
    },
    availableOptions() {
      const translateIfNeeded = (opt) => {
        //  Traduire D'ABORD, puis capitaliser
        const rawLabel = opt.label || opt.libelle || opt.name || opt.nom
        let label = rawLabel
        
        // 1. Traduire si c'est une clé de traduction
        if (rawLabel && String(rawLabel).startsWith('general.')) {
          label = this.$t(rawLabel)
        }
        
        // 2. Capitaliser la première lettre APRÈS traduction
        if (label && typeof label === 'string' && label.length > 0) {
          label = label.charAt(0).toUpperCase() + label.slice(1)
        }
        
        return {
          ...opt,
          label
        }
      }
      
      //  Si la source est de type 'store', récupérer depuis le Vuex store
      if (this.field && this.field.dataSource && this.field.dataSource.type === 'store') {
        const storeData = this.getStoreData()
        if (storeData && storeData.length > 0) {
          return this.formatStoreData(storeData).map(translateIfNeeded)
        }
      }
      
      //  ARCHITECTURE BACKEND-FIRST : Utiliser les données déjà résolues
      if (this.field && this.field.dataSource && Array.isArray(this.field.dataSource.data)) {
        return this.field.dataSource.data.map(translateIfNeeded)
      }

      //  Fallback: options métier inline (pour champs comme typeObjets CMI)
      if (this.field && this.field.properties && Array.isArray(this.field.properties.options)) {
        return this.field.properties.options.map(translateIfNeeded)
      }

      // Fallback: options ajoutées par le parent via staticOptions
      if (this.field && Array.isArray(this.field.staticOptions)) {
        return this.field.staticOptions.map(translateIfNeeded)
      }

      //  PLUS DE FALLBACK HARDCODÉ - Tout doit venir du backend
      console.warn(` [SearchFieldRadio] Aucune option trouvée pour le champ ${this.field.name}. Toutes les données doivent venir du backend.`)
      return []
    }
  },
  methods: {
    translate(key) {
      if (!key) return ''
      let translated = String(key).startsWith('general.') ? this.$t(key) : key
      // Capitaliser la première lettre
      if (translated && typeof translated === 'string' && translated.length > 0) {
        translated = translated.charAt(0).toUpperCase() + translated.slice(1)
      }
      return translated
    },
    handleInput(inputValue) {
      this.$emit('input', inputValue)
    },

    handleChange(selectedValue) {
      this.$emit('input', selectedValue)
      this.$emit('change', { field: this.field.name, value: selectedValue })
    },

    //  getDefaultOptions() - Toutes les données viennent maintenant du backend

    getProperty(propertyName, defaultValue = undefined) {
      if (!this.field.properties || !Array.isArray(this.field.properties)) {
        return defaultValue
      }

      const property = this.field.properties.find(p => p.name === propertyName)
      if (!property) {
        return defaultValue
      }

      switch (property.type) {
        case 'boolean':
          return property.value === 'true' || property.value === true
        case 'number':
        case 'integer':
          return property.value ? Number(property.value) : defaultValue
        case 'string':
        default:
          return property.value || defaultValue
      }
    },
    
    //  Méthodes pour gérer les sources de type 'store'
    getStoreData() {
      // Les paramètres sont dans field.dataSource.parameters (venant du backend)
      const parameters = this.field.dataSource.parameters || {}
      const storePath = parameters.storePath || this.field.dataSource.storePath
      const storeGetter = parameters.storeGetter || this.field.dataSource.storeGetter
      
      
      // Utiliser le getter si disponible (ex: 'stocks')
      if (storeGetter && this.$store.getters[storeGetter]) {
        const data = this.$store.getters[storeGetter]
        return data
      }
      
      // Ou accéder directement au state avec un chemin (ex: 'sitimmoUser.stocks')
      if (storePath) {
        const parts = storePath.split('.')
        let data = this.$store.state
        for (const part of parts) {
          data = data && data[part]
        }
        return data
      }
      
      console.warn(` [SearchFieldRadio.getStoreData] Aucune source trouvée pour ${this.field.name}`)
      return []
    },
    
    formatStoreData(storeData) {
      // Récupérer les champs configurés dans les paramètres du backend
      const parameters = this.field.dataSource.parameters || {}
      const valueField = parameters.valueField || 'value'
      const labelField = parameters.labelField || 'label'
      
      
      // Format générique basé sur la configuration backend
      return storeData.map(item => {
        const rawLabel = item[labelField] || item.label || item.libelle || item.name || item.nom || item.value
        const option = {
          value: item[valueField] || item.value || item.code || item.id,
          label: this.capitalizeFirstLetter(rawLabel)
        }
        console.log(`  → Option:`, option)
        return option
      })
    },
    
    //  Fonction utilitaire pour capitaliser la première lettre
    capitalizeFirstLetter(str) {
      if (!str || typeof str !== 'string') return str
      return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
    }
  }
}
</script>

<style scoped>
.search-field {
  margin-bottom: 16px;
}

.search-field-required .el-form-item__label::after {
  content: ' *';
  color: #f56c6c;
}

.search-field-disabled {
  opacity: 0.6;
}

.field-help-text {
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.4;
}

.text-muted {
  color: #909399;
}

/* Styles pour les radios compacts */
.radio-compact {
  display: block !important;
  margin-bottom: 4px !important;
  line-height: 1.3 !important;
  height: auto !important;
  padding: 2px 0 !important;
}

.radio-compact >>> .el-radio__input {
  transform: scale(0.9);
  margin-right: 6px !important;
}

.radio-compact >>> .el-radio__label {
  font-size: 14px !important;
  line-height: 1.3 !important;
  padding-left: 0 !important;
}

/* Styles pour les radios inline (par défaut) */
.radio-inline {
  margin-right: 20px;
  margin-bottom: 8px;
}

/* Force l'affichage vertical pour tous les radio groups */
::v-deep .el-radio-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

::v-deep .el-radio {
  display: block;
  margin-right: 0;
  margin-bottom: 0;
}

.radio-inline:last-child {
  margin-right: 0;
}

/* Styles pour les radio-buttons */
.search-field-radio .el-radio-button {
  margin-right: 8px;
}

.search-field-radio .el-radio-button:last-child {
  margin-right: 0;
}

/* Styles responsifs */
@media (max-width: 768px) {
  .radio-inline {
    display: block;
    margin-bottom: 12px;
    margin-right: 0;
  }
  
  .search-field-radio .el-radio-button {
    display: block;
    width: 100%;
    margin-bottom: 8px;
    margin-right: 0;
  }
}
</style>
